import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import { postAuth, putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { FundCampaignDtoIn } from '@/models/in/FundCampaignDtoIn';
import { FundCampaignDtoOut } from '@/models/out/FundCampaignDtoOut';
import { campaignsUrl, isWebAddress, toDateInputValue } from './fundCampaigns';
import styles from './FundDialogs.module.css';

/** Mirrors the column widths, so the server never rejects something the field could have stopped. */
const MAX_NAME = 200;
const MAX_NUMBER = 500;
const MAX_URL = 1000;

type FieldName = 'name' | 'campaignNumber' | 'startDate' | 'url';

interface EditOrCreateCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  fundId: number;
  /** null when adding. */
  campaignToEdit: FundCampaignDtoIn | null;
}

const serverError = (error: unknown): string | null => {
  const data = (error as AxiosError).response?.data;
  return typeof data === 'string' && data ? data : null;
};

const EditOrCreateCampaignDialog = ({ open, onClose, onSaved, fundId, campaignToEdit }: EditOrCreateCampaignDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  const isEditing = campaignToEdit !== null;

  const [name, setName] = useState('');
  const [campaignNumber, setCampaignNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [url, setUrl] = useState('');
  /** Which field the message belongs to; null for a message about the form as a whole. */
  const [invalidField, setInvalidField] = useState<FieldName | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // The dialog is mounted once per fund and reused, so every open starts from the campaign it was
  // opened for - not from whatever the previous one left behind.
  useEffect(() => {
    if (!open) return;
    setName(campaignToEdit?.name ?? '');
    setCampaignNumber(campaignToEdit?.campaignNumber ?? '');
    setStartDate(toDateInputValue(campaignToEdit?.startDate));
    setUrl(campaignToEdit?.url ?? '');
    setInvalidField(null);
    setError(null);
  }, [open, campaignToEdit]);

  const reject = (field: FieldName, key: string) => {
    setInvalidField(field);
    setError(t(key));
  };

  /** Everything the client can judge for itself, so a rejection points at a field. */
  const findProblem = (): boolean => {
    if (!name.trim()) {
      reject('name', 'fundsManagement.campaigns.validation.nameRequired');
      return true;
    }
    if (!campaignNumber.trim()) {
      reject('campaignNumber', 'fundsManagement.campaigns.validation.numberRequired');
      return true;
    }
    if (!startDate) {
      reject('startDate', 'fundsManagement.campaigns.validation.startDateRequired');
      return true;
    }
    if (url.trim() && !isWebAddress(url.trim())) {
      reject('url', 'fundsManagement.campaigns.validation.urlInvalid');
      return true;
    }
    return false;
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setInvalidField(null);
    setError(null);
    if (findProblem()) return;

    // Sent as typed; the server is the one that adds a missing scheme, so there is a single
    // authority on what actually gets stored.
    const body: FundCampaignDtoOut = {
      name: name.trim(),
      campaignNumber: campaignNumber.trim(),
      startDate,
      url: url.trim() || null,
    };

    setIsSaving(true);
    try {
      if (isEditing) {
        await putAuth<FundCampaignDtoIn, object, FundCampaignDtoOut>(
          `${campaignsUrl(fundId)}/${campaignToEdit.id}`, headers, {}, body
        );
      } else {
        await postAuth<FundCampaignDtoIn, FundCampaignDtoOut>(campaignsUrl(fundId), headers, body);
      }
      onSaved();
      onClose();
    } catch (err) {
      // What is left for the server to refuse is a duplicate number or a missing fund - neither
      // belongs to one field the way a validation message does, so it is shown for the form.
      setError(serverError(err) ?? t('fundsManagement.campaigns.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={isEditing ? t('fundsManagement.campaigns.editTitle') : t('fundsManagement.campaigns.addTitle')}
      contentClassName={`${styles.wideDialogContent} ${styles.autoHeightDialog}`}
    >
      {/* A real form, so Enter saves instead of doing nothing. */}
      <form className={`${styles.dialogForm} ${styles.campaignForm}`} onSubmit={save} noValidate>
        <div className={styles.formField}>
          <label className={styles.fieldLabel} htmlFor="campaign-name">
            {t('fundsManagement.campaigns.name')}
          </label>
          {/* autoFocus: the dialog opens for the sole purpose of filling this form, so the first
              field takes focus rather than making every user tab into it. */}
          <Input
            id="campaign-name"
            autoFocus
            maxLength={MAX_NAME}
            error={invalidField === 'name'}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className={styles.twoFieldsRow}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="campaign-number">
              {t('fundsManagement.campaigns.number')}
            </label>
            <Input
              id="campaign-number"
              dir="ltr"
              maxLength={MAX_NUMBER}
              error={invalidField === 'campaignNumber'}
              value={campaignNumber}
              onChange={(e) => setCampaignNumber(e.target.value)}
              aria-describedby="campaign-number-hint"
            />
            {/* Under the field it explains: this is the only value here with a consequence
                outside the dialog. */}
            <span id="campaign-number-hint" className={styles.fieldHint}>
              {t('fundsManagement.campaigns.numberHint')}
            </span>
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="campaign-start">
              {t('fundsManagement.campaigns.startDate')}
            </label>
            <Input
              id="campaign-start"
              type="date"
              error={invalidField === 'startDate'}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.formField}>
          <label className={styles.fieldLabel} htmlFor="campaign-url">
            {t('fundsManagement.campaigns.url')}
            <span className={styles.optionalTag}>{t('fundsManagement.campaigns.optional')}</span>
          </label>
          <Input
            id="campaign-url"
            dir="ltr"
            maxLength={MAX_URL}
            error={invalidField === 'url'}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('fundsManagement.campaigns.urlPlaceholder')}
          />
        </div>

        {error && <p className={styles.formError} role="alert">{error}</p>}

        <div className={styles.formActions}>
          <Button type="button" variant="white" onClick={onClose} disabled={isSaving}>
            {t('fundsManagement.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving}>
            {isSaving ? t('loading') : t('fundsManagement.save')}
          </Button>
        </div>
      </form>
    </MainDialogLayout>
  );
};

export default EditOrCreateCampaignDialog;
